'use client'

import Image from 'next/image'
import React from 'react'

import { type PropsWithClassName } from '@helper/PropsWithClassName'

import { Coupon as CouponType, Studio } from '@definitions/types/symfonyTypes'

import { cn } from '@helper/utils/cn'

import { CouponItem } from './components/CouponItem'
import { useFilteredCoupons } from './hooks/useFilteredCoupons'
import { useCampaignPopup } from './hooks/useCampaignPopup'
import { useThemeKey } from './hooks/useThemeKey'
import { createCouponImageUrl } from './utils/createCouponImageUrl'
import { isStudioEligible } from './utils/isStudioEligible'

interface CouponProps extends PropsWithClassName {
  coupons: CouponType[]
  studio: Studio
  themeKey?: 'sunpoint' | 'wellmaxx'
}

export function Coupon({
  coupons,
  studio,
  themeKey,
  className,
}: CouponProps) {
  // Use useThemeKey hook to get theme when not provided as prop
  const resolvedThemeKey = useThemeKey(themeKey)
  const couponsFiltered = useFilteredCoupons(coupons, resolvedThemeKey)
  const campaignPopup = useCampaignPopup(resolvedThemeKey)
  const isEligible = isStudioEligible(studio)

  return (
    <div
      className={cn('grid grid-cols-1 md:grid-cols-2 gap-6 mb-8', className)}
      data-testid="coupons-root"
    >
      {couponsFiltered.map((coupon) => {
        return (
          <CouponItem
            key={coupon.id}
            formId={coupon.id}
            coupon={coupon}
            studio={studio}
            themeKey={resolvedThemeKey}
            isEligible={isEligible}
            campaignPopup={campaignPopup ?? undefined}
          >
            {coupon.frontendImage?.publicPath && (
              <Image
                src={createCouponImageUrl(coupon)}
                alt={coupon.title}
                width={608}
                height={217}
                className="w-full h-auto"
              />
            )}
          </CouponItem>
        )
      })}
    </div>
  )
}
